import type { Page } from "@inertiajs/core";

type EntityId = number | string | bigint;
export type WorkspaceMutationScope = { companyId: EntityId; userId: EntityId };
export type ConversationMutationScope = WorkspaceMutationScope & { conversationId: EntityId };
type MutationPage = Page<{
  auth?: { company?: { id: EntityId } | null; user?: { id: EntityId } | null };
  activeConversation?: { id: EntityId } | null;
}>;

// Capture these IDs when subscribing/starting asynchronous work, NOT when its
// completion runs. Inertia's patched guard is checked before evaluating the
// buffered props intent. A failed guard does not call page.set or onSuccess.
export function workspaceMutationGuard({ companyId, userId }: WorkspaceMutationScope) {
  return ({ props }: MutationPage): boolean =>
    props.auth?.company?.id !== undefined &&
    props.auth.company.id !== null &&
    props.auth?.user?.id !== undefined &&
    props.auth.user.id !== null &&
    String(props.auth.company.id) === String(companyId) &&
    String(props.auth.user.id) === String(userId);
}

export function conversationMutationGuard(scope: ConversationMutationScope) {
  const workspaceMatches = workspaceMutationGuard(scope);
  const conversationId = String(scope.conversationId);
  return (page: MutationPage): boolean =>
    workspaceMatches(page) &&
    page.props.activeConversation?.id !== undefined &&
    page.props.activeConversation.id !== null &&
    String(page.props.activeConversation.id) === conversationId;
}

function sameOptionalId(
  left: number | string | bigint | null | undefined,
  right: number | string | bigint,
) {
  return left !== null && left !== undefined && Number(left) === Number(right);
}

export type WorkspaceUnreadItem = {
  id?: number | string | bigint;
  conversationId?: number | string | bigint | null;
  unreadCount?: number;
  lastMessageAt?: string | null;
};

// Shared by workspace-shell's sidebar producers and the coalesced mark-read
// write in conversation.tsx: only the entry for `conversationId` is touched,
// preserving unread counts for every other conversation.
export function clearWorkspaceUnreadForConversation<TItem extends WorkspaceUnreadItem>(
  items: TItem[] = [],
  conversationId: number,
  observedThrough?: string | null,
): TItem[] {
  return items.map((item) =>
    sameOptionalId(
      item.conversationId === undefined ? item.id : item.conversationId,
      conversationId,
    ) && canClearObservedUnread(item.lastMessageAt, observedThrough)
      ? { ...item, unreadCount: 0 }
      : item,
  );
}

// A client watermark is NOT the server's read boundary. It is only a safe
// lower bound for this optimistic clear: preserve newer/unknown activity and
// let the existing authoritative sidebar poll reconcile it. Undefined is for
// callers performing an unconditional authoritative clear (not POST callbacks).
export function canClearObservedUnread(
  lastMessageAt: string | null | undefined,
  observedThrough: string | null | undefined,
): boolean {
  if (observedThrough === undefined) {
    return true;
  }
  if (!lastMessageAt) {
    return false;
  }
  if (!observedThrough) {
    return false;
  }
  return Date.parse(lastMessageAt) <= Date.parse(observedThrough);
}

type PresenceMember = {
  presenceExpiresAt?: string | null;
  presenceStatus?: string | null;
};

// Cheap preflight for the presence-expiry timer: lets the caller skip
// `router.replaceProp` entirely on ticks where nothing actually expired,
// instead of relying only on the patched core's post-hoc no-op detection.
export function hasExpiredPresenceMember<TMember extends PresenceMember>(
  members: TMember[],
  now: number,
): boolean {
  return members.some((member) => {
    if (!member.presenceExpiresAt || member.presenceStatus === "offline") {
      return false;
    }
    const expiresAt = Date.parse(member.presenceExpiresAt);
    return Number.isFinite(expiresAt) && expiresAt <= now;
  });
}
